import { supabase } from './supabase';

/**
 * Отримати налаштування Telegram бота з бази даних
 */
export const getTelegramSettings = async () => {
  try {
    const { data, error } = await supabase
      .from('telegram_settings')
      .select('*')
      .eq('is_active', true)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting Telegram settings:', error);
    return null;
  }
};

/**
 * Оновити налаштування Telegram бота
 */
export const updateTelegramSettings = async (botToken, chatUsername) => {
  try {
    // Отримати існуючі налаштування
    const { data: existing } = await supabase
      .from('telegram_settings')
      .select('id')
      .limit(1)
      .single();

    if (existing) {
      // Оновити існуючі
      const { data, error } = await supabase
        .from('telegram_settings')
        .update({
          bot_token: botToken,
          chat_username: chatUsername,
          updated_at: new Date().toISOString()
        })
        .eq('id', existing.id)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } else {
      // Створити нові
      const { data, error } = await supabase
        .from('telegram_settings')
        .insert([{
          bot_token: botToken,
          chat_username: chatUsername,
          is_active: true
        }])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    }
  } catch (error) {
    console.error('Error updating Telegram settings:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Отримати Chat ID за username
 */
const getChatId = async (botToken, username) => {
  try {
    // Специальная обработка для @crystal_dental
    if (username === '@crystal_dental' || username === 'crystal_dental') {
      return 762068690;
    }

    // Спробувати отримати updates
    const response = await fetch(`https://api.telegram.org/bot${botToken}/getUpdates`);
    const data = await response.json();

    if (!data.ok) {
      throw new Error('Failed to get updates from Telegram');
    }

    // Знайти chat_id для username
    const updates = data.result || [];
    for (const update of updates.reverse()) {
      const chat = update.message?.chat || update.my_chat_member?.chat;
      if (chat && chat.username === username.replace('@', '')) {
        return chat.id;
      }
    }

    // Якщо не знайдено в updates, повернути username як є
    // (користувач має спочатку написати боту)
    return username;
  } catch (error) {
    console.error('Error getting chat ID:', error);
    return username;
  }
};

/**
 * Відправити повідомлення в Telegram
 */
export const sendTelegramNotification = async (booking) => {
  try {
    // Отримати налаштування
    const settings = await getTelegramSettings();
    if (!settings) {
      return { success: false, error: 'Settings not configured' };
    }

    const { bot_token, chat_username } = settings;

    // Отримати chat_id
    const chatId = await getChatId(bot_token, chat_username);

    // Форматувати повідомлення
    const createdAt = booking.created_at || new Date().toISOString();
    const message = `
🦷 *Новий запис в Crystal Dental Studio*

👤 *Пацієнт:* ${booking.patient_name}
📞 *Телефон:* ${booking.patient_phone}
${booking.patient_email ? `📧 *Email:* ${booking.patient_email}\n` : ''}
👨‍⚕️ *Лікар:* ${booking.doctor_name}
💼 *Послуга:* ${booking.service_title}
📅 *Дата:* ${formatDate(booking.booking_date)}
⏰ *Час:* ${booking.booking_time}

_Запис створено: ${formatDateTime(createdAt)}_
    `.trim();

    // Відправити повідомлення
    const response = await fetch(`https://api.telegram.org/bot${bot_token}/sendMessage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chat_id: chatId,
        text: message,
        parse_mode: 'Markdown'
      })
    });

    const data = await response.json();

    if (!data.ok) {
      throw new Error(data.description || 'Failed to send Telegram message');
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Тестувати підключення до Telegram бота
 */
export const testTelegramConnection = async (botToken, chatUsername) => {
  try {
    // Перевірити токен бота
    const botResponse = await fetch(`https://api.telegram.org/bot${botToken}/getMe`);
    const botData = await botResponse.json();

    if (!botData.ok) {
      throw new Error('Невірний токен бота');
    }

    // Спробувати отримати chat_id (включая специальную обработку для @crystal_dental)
    const chatId = await getChatId(botToken, chatUsername);

    // Відправити тестове повідомлення
    const testMessage = `✅ Тестове повідомлення від Crystal Dental Studio\n\nБот успішно підключено!\nІм'я бота: ${botData.result.first_name}`;

    const messageResponse = await fetch(`https://api.telegram.org/bot${botToken}/sendMessage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chat_id: chatId,
        text: testMessage
      })
    });

    const messageData = await messageResponse.json();

    if (!messageData.ok) {
      if (messageData.description?.includes('chat not found')) {
        throw new Error('Користувач не знайдений. Спочатку напишіть боту /start');
      }
      throw new Error(messageData.description || 'Помилка відправки повідомлення');
    }

    return {
      success: true,
      botName: botData.result.first_name,
      botUsername: botData.result.username
    };
  } catch (error) {
    console.error('Error testing Telegram connection:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Допоміжні функції форматування
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('uk-UA', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('uk-UA', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};
